class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.compareSubtotalNode = this.drawer.querySelector('[data-cart-drawer-compare-subtotal]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.priceOrder = this.drawer.dataset.cartDrawerPriceOrder || 'sale_first';
    this.showSavings = this.drawer.dataset.cartDrawerShowSavings === 'true';
    this.savingsItemTemplate = this.drawer.dataset.cartDrawerSavingsItemTemplate || '';
    this.savingsTotalTemplate = this.drawer.dataset.cartDrawerSavingsTotalTemplate || '';
    this.summarySavingsNode = this.drawer.querySelector('[data-cart-drawer-summary-savings]');
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || '';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || '';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || '';
    this.continueUrl = this.drawer.dataset.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = this.drawer.dataset.cartDrawerContinueLabel || '';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || '';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || '';
    this.quantityLabel = this.drawer.dataset.cartDrawerQuantityLabel || '';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || '';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || '';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || '';
    this.liveRegionTemplate = this.drawer.dataset.cartDrawerLiveTemplate || '';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || '';
    this.addLabel = this.drawer.dataset.cartDrawerAddLabel || 'Add';
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';

    // Free shipping progress bar
    this.shippingBar = this.drawer.querySelector('[data-cart-drawer-shipping-bar]');
    this.shippingMessage = this.drawer.querySelector('[data-cart-drawer-shipping-message]');
    this.shippingFill = this.drawer.querySelector('[data-cart-drawer-shipping-fill]');
    this.shippingEnabled = this.drawer.dataset.cartDrawerShippingEnable === 'true';
    this.shippingThreshold = Number(this.drawer.dataset.cartDrawerShippingThreshold || 0);
    this.shippingRemainingTemplate = this.drawer.dataset.cartDrawerShippingRemainingTemplate || '';
    this.shippingCompleteTemplate = this.drawer.dataset.cartDrawerShippingCompleteTemplate || '';
    this.shippingReward = this.drawer.dataset.cartDrawerShippingReward || '';

    // Cart upsell / recommendations
    this.upsellSection = this.drawer.querySelector('[data-cart-drawer-upsell]');
    this.upsellList = this.drawer.querySelector('[data-cart-drawer-upsell-list]');
    this.upsellEnabled = this.drawer.dataset.cartDrawerUpsellEnable === 'true';
    this.upsellLimit = Number(this.drawer.dataset.cartDrawerUpsellLimit || 4);
    this.upsellSourceNodes = null;

    // Shipping protection add-on (cart-level toggle, kept separate from the
    // shipping rate itself so "livraison offerte" stays intact everywhere).
    this.protectionSection = this.drawer.querySelector('[data-cart-protection]');
    this.protectionCheckbox = this.drawer.querySelector('[data-cart-protection-checkbox]');
    this.protectionVariantId = Number(this.protectionCheckbox?.dataset.cartProtectionVariantId || 0);
    this.protectionPending = false;
    this.protectionOptedOut = false;
    try { this.protectionOptedOut = sessionStorage.getItem('pb-protection-opt-out') === 'true'; } catch (_) {}
    this.clearingOrphanProtection = false;

    this.bindUI();
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    if (this.activeAddButton?.disabled) return;
    const formData = new FormData(form);
    const bundle = form.querySelector('[data-bundle-pricing]');
    if (bundle) {
      const product = JSON.parse(form.closest('[data-product-section]').querySelector('[data-product-json]').textContent);
      const mainId = Number(formData.get('id'));
      const main = product.variants.find(v => v.id === mainId);
      if (!main) {
        this.showAddToCartError(form, 'Veuillez sélectionner une variante disponible.');
        return;
      }
      // Exclude the shared main <select> (data-bundle-main-select): it also carries
      // data-bundle-tier-extra-select so the widget's own tier-switching script (in
      // bundle-pricing.liquid) can treat every row generically, but it must NOT be
      // treated as an "extra unit" selection here — it's already accounted for via
      // `main` above. Including it would double-count the main variant.
      const selections = [...bundle.querySelectorAll('[data-bundle-tier-extra-select]:not(:disabled):not([data-bundle-main-select])')];
      const variants = [main, ...selections.map(select => product.variants.find(v =>
        v.options[0] === select.value && v.options.slice(1).every((value, i) => value === main.options[i + 1])
      ))];
      if (variants.some(v => !v || !v.available)) {
        this.showAddToCartError(form, 'Une variante sélectionnée est indisponible. Veuillez modifier votre sélection.');
        return;
      }
      formData.delete('id');
      formData.delete('quantity');
      selections.forEach(select => formData.delete(select.name));
      const quantities = new Map();
      variants.forEach(v => quantities.set(v.id, (quantities.get(v.id) || 0) + 1));
      [...quantities].forEach(([id, quantity], i) => {
        formData.set(`items[${i}][id]`, id);
        formData.set(`items[${i}][quantity]`, quantity);
      });
    }
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);
    this.hideAddToCartError(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }

      await response.json();
      const cart = await this.refresh();
      this.open();
      if (!cart) this.showCartError('Articles ajoutés. Rechargez le panier pour voir son contenu à jour.');
    } catch (error) {
      console.error(error);
      this.showAddToCartError(form, error?.message);
    } finally {
      this.setAddButtonState(false);
    }
  }

  showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (!errorElement) {
      window.location.href = form.action || '/cart';
      return;
    }

    errorElement.textContent = message || errorElement.dataset.genericError || '';
    errorElement.hidden = false;
  }

  hideAddToCartError(form) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.value ?? quantityValue?.textContent ?? 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);

      await this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      await this.changeItem(key, 0);
      return;
    }

    const upsellAddTrigger = event.target.closest('[data-cart-drawer-upsell-add]');
    if (upsellAddTrigger) {
      await this.addUpsellItem(upsellAddTrigger);
    }
  }

  async handleDrawerChange(event) {
    const protectionToggle = event.target.closest('[data-cart-protection-checkbox]');
    if (protectionToggle) {
      await this.toggleProtection(protectionToggle);
      return;
    }

    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    if (!item) return;

    const key = item.dataset.cartItemKey;
    if (!key || this.pendingItemKeys.has(key)) return;

    const quantity = Number(input.value);
    if (!input.value.trim() || !Number.isSafeInteger(quantity) || quantity < 1) {
      input.value = input.defaultValue;
      this.showCartError('Veuillez saisir un nombre entier supérieur ou égal à 1.');
      return;
    }
    await this.changeItem(key, quantity);
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.showCartError('');
    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || 'Impossible de modifier la quantité.');
      }

      let cart = await response.json();
      cart = await this.ensureBundleGifts(cart);
      cart = await this.ensureDefaultProtection(cart);
      this.render(cart);
    } catch (error) {
      console.error(error);
      this.pendingItemKeys.delete(key);
      await this.refresh();
      this.showCartError(error.message || 'Impossible de modifier la quantité.');
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  async toggleProtection(checkbox) {
    if (this.protectionPending) return;
    const variantId = Number(checkbox.dataset.cartProtectionVariantId);
    if (!variantId) return;

    this.protectionPending = true;
    checkbox.disabled = true;
    this.showCartError('');
    const wantsChecked = checkbox.checked;
    this.protectionOptedOut = !wantsChecked;
    try { sessionStorage.setItem('pb-protection-opt-out', String(!wantsChecked)); } catch (_) {}

    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('Cart unavailable');
      const currentCart = await cartResponse.json();
      const existingItem = (currentCart.items || []).find((item) => Number(item.variant_id) === variantId);

      let response;
      if (wantsChecked && !existingItem) {
        response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
      } else if (!wantsChecked && existingItem) {
        response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: existingItem.key, quantity: 0 })
        });
      }

      if (response && !response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }

      await this.refresh();
    } catch (error) {
      console.error(error);
      checkbox.checked = !wantsChecked;
      this.showCartError('Impossible de mettre à jour la protection colis. Vérifiez votre connexion.');
    } finally {
      this.protectionPending = false;
      checkbox.disabled = false;
    }
  }

  syncProtectionToggle(cart) {
    if (!this.protectionSection) return;

    this.protectionSection.hidden = (cart.item_count || 0) === 0 || cart.items.some(item => item.properties?._discelo_tier === 'fire');

    if (this.protectionCheckbox && !this.protectionPending) {
      const isInCart = (cart.items || []).some((item) => Number(item.variant_id) === this.protectionVariantId);
      this.protectionCheckbox.checked = isInCart;
    }
  }

  maybeClearOrphanProtection(cart) {
    if (!this.protectionVariantId || this.clearingOrphanProtection) return;

    const protectionItem = (cart.items || []).find((item) => Number(item.variant_id) === this.protectionVariantId);
    if (!protectionItem) return;

    const realItemCount = (cart.items || []).reduce(
      (sum, item) => (Number(item.variant_id) === this.protectionVariantId ? sum : sum + item.quantity),
      0
    );
    if (realItemCount > 0) return;

    // The protection add-on only makes sense alongside a real product — if the
    // customer removed every other item while it was toggled on, drop it too
    // instead of leaving a lone "Protection colis" charge in an otherwise
    // empty-looking cart.
    this.clearingOrphanProtection = true;
    fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: protectionItem.key, quantity: 0 })
    })
      .then((response) => response.json())
      .then((data) => {
        this.clearingOrphanProtection = false;
        if (Array.isArray(data.items)) this.render(data);
      })
      .catch(() => {
        this.clearingOrphanProtection = false;
      });
  }

  async addUpsellItem(button) {
    if (button.disabled) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.showCartError('');
    button.disabled = true;
    button.classList.add('is-adding');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }

      // On success the cart re-render below replaces this button's markup
      // entirely, so there's nothing left to reset here.
      const cart = await this.refresh();
      if (cart) this.open();
      else this.showCartError('Article ajouté. Rechargez le panier pour voir son contenu à jour.');
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.classList.remove('is-adding');
      this.showCartError(error.message || 'Impossible d’ajouter cet article. Vérifiez votre connexion.');
    }
  }

  showSkeleton(count = 2) {
    if (!this.body) return;

    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines">
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
        </div>
      </div>
    `).join('');

    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  showCartError(message) {
    if (!this.errorNode) {
      this.errorNode = document.createElement('p');
      this.errorNode.setAttribute('role', 'alert');
      this.errorNode.style.cssText = 'margin:12px 20px;color:#a32020;font-size:14px;';
      this.body.before(this.errorNode);
    }
    this.errorNode.textContent = message;
    this.errorNode.hidden = !message;
  }

  async refresh() {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.loadCart();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async ensureBundleGifts(cart) {
    const fan = 59635531612508, fire = 59642841301340;
    const necklace = 59642917224796, portrait = 59642917257564, bracelet = 59642917290332;
    const wanted = new Map([[necklace,0],[portrait,0],[bracelet,0]]);
    for (const item of cart.items || []) {
      if (Number(item.variant_id) === fan) wanted.set(necklace, wanted.get(necklace) + item.quantity);
      if (Number(item.variant_id) === fire) {
        for (const id of wanted.keys()) wanted.set(id, wanted.get(id) + item.quantity);
      }
    }
    const updates = {}, additions = [];
    for (const item of cart.items || []) {if (item.properties?._discelo_gift === 'true' && !wanted.has(Number(item.variant_id))) updates[item.key] = 0;}
    for (const [id, qty] of wanted) {
      const existing = cart.items.filter(item => Number(item.variant_id) === id && item.properties?._discelo_gift === 'true');
      if (!existing.length && qty) additions.push({id, quantity:qty, properties:{_discelo_gift:'true', 'Cadeau':'Offert avec votre coffret'}});
      existing.forEach((item,index) => {const next = index === 0 ? qty : 0; if (item.quantity !== next) updates[item.key] = next;});
    }
    if (Object.keys(updates).length) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify({updates})
      });
      if (!response.ok) throw new Error('Impossible de synchroniser les cadeaux.');
      cart = await response.json();
    }
    if (additions.length) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify({items:additions})
      });
      if (!response.ok) throw new Error('Impossible d’ajouter les cadeaux. Veuillez réessayer.');
      const updated = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {headers:{Accept:'application/json'}});
      if (!updated.ok) throw new Error('Panier indisponible.');
      cart = await updated.json();
    }
    const chargedGift = cart.items.some(item => item.properties?._discelo_gift === 'true' && item.final_line_price > 0);
    if (chargedGift) throw new Error('La gratuité des cadeaux n’a pas pu être appliquée. Veuillez actualiser le panier.');
    return cart;
  }

  async ensureDefaultProtection(cart) {
    const tiers = cart.items.filter(item => item.properties?._discelo_tier);
    if (tiers.some(item => item.properties._discelo_tier === 'fire')) {
      const paid = cart.items.find(item => Number(item.variant_id) === this.protectionVariantId);
      if (paid) {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST', headers: {'Content-Type':'application/json', Accept:'application/json'},
          body: JSON.stringify({id:paid.key, quantity:0})
        });
        if (!response.ok) throw new Error('Impossible de retirer la protection payante.');
        return response.json();
      }
      return cart;
    }
    // Default protection applies to small and medium; premium remains included.
    if (!this.protectionVariantId || this.protectionOptedOut || this.protectionPending ||
        !cart.items.some(item => Number(item.variant_id) !== this.protectionVariantId && item.properties?._discelo_gift !== 'true') ||
        cart.items.some(item => Number(item.variant_id) === this.protectionVariantId)) return cart;
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: this.protectionVariantId, quantity: 1 })
    });
    if (!response.ok) return cart;
    const updated = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!updated.ok) throw new Error('Cart unavailable');
    return updated.json();
  }

  async loadCart() {
    const previousMarkup = this.body.innerHTML;
    const currentCount = Number(this.drawerCount?.textContent || 0);
    if (currentCount > 0) this.showSkeleton(Math.min(currentCount, 3));
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart unavailable');
      let cart = await response.json();
      cart = await this.ensureBundleGifts(cart);
      cart = await this.ensureDefaultProtection(cart);
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
      this.render(cart);
      this.drawer.querySelectorAll('a[href*="/checkout"],button[name="checkout"]').forEach(node => {node.removeAttribute('aria-disabled');node.style.pointerEvents='';});
      this.showCartError('');
      return cart;
    } catch (error) {
      this.body.innerHTML = previousMarkup;
      this.drawer.querySelectorAll('a[href*="/checkout"],button[name="checkout"]').forEach(node => {node.setAttribute('aria-disabled','true');node.style.pointerEvents='none';});
      this.showCartError('Impossible d’actualiser le panier. Vérifiez votre connexion puis rouvrez-le.');
      return null;
    }
  }

  render(cart) {
    this.hydrateCompareAtPrices(cart);
    const itemCount = cart.item_count || 0;

    if (this.drawerCount) {
      this.drawerCount.textContent = itemCount;
    }

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.subtotalNode) {
      this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    }

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    const compareTotal = (cart.items || []).reduce(
      (sum, item) => sum + (item.compare_at_line_price || item.final_line_price || 0),
      0
    );
    if (this.compareSubtotalNode) {
      const showCompareSubtotal = this.showCompareAt && compareTotal > (cart.total_price || 0);
      this.compareSubtotalNode.hidden = !showCompareSubtotal;
      this.compareSubtotalNode.textContent = showCompareSubtotal ? this.formatMoney(compareTotal) : '';
    }

    if (this.summarySavingsNode) {
      const cartSavings = compareTotal - (cart.total_price || 0);
      const showCartSavings = this.showCompareAt && this.showSavings && cartSavings > 0;
      this.summarySavingsNode.hidden = !showCartSavings;
      this.summarySavingsNode.textContent = showCartSavings
        ? this.savingsTotalTemplate.replace('[[amount]]', this.formatMoney(cartSavings))
        : '';
    }

    if (this.footer) {
      this.footer.hidden = itemCount === 0;
    }

    this.updateShippingBar(cart);
    this.updateUpsell(cart);
    this.syncProtectionToggle(cart);

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
          <a href="${this.escapeHtml(this.continueUrl)}" class="button button--primary cart-drawer__empty-cta">
            ${this.escapeHtml(this.continueLabel)}
          </a>
        </div>
      `;
      return;
    }

    // The protection add-on is a real cart line (so pricing/taxes are accurate
    // at checkout), but it's represented by the dedicated toggle above, not as
    // a normal product row with quantity steppers — so it's excluded here.
    const visibleItems = cart.items.filter((item) => Number(item.variant_id) !== this.protectionVariantId).sort((a,b) => Number(a.properties?._discelo_gift === 'true') - Number(b.properties?._discelo_gift === 'true'));

    const itemsMarkup = visibleItems
      .map((item) => {
        const imageMarkup = item.image
          ? `<a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media">
              <img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy">
            </a>`
          : `<div class="cart-drawer__item-media"></div>`;

        const variantMarkup =
          item.properties?._discelo_gift !== 'true' && item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>`
            : '';

        // Line item properties (e.g. the extra Sourate chosen for unit 2/3 of a
        // bundle) aren't reflected in variant_title, so surface them here too —
        // mirrors the [hidden]-prefixed-key skip that snippets/cart-drawer.liquid
        // applies on full page loads.
        const propertiesMarkup = Object.entries(item.properties || {})
          .filter(([key, value]) => value !== null && value !== undefined && value !== '' && !key.startsWith('_') && !(key === 'Cadeaux inclus' && [59635531612508,59642841301340].includes(Number(item.variant_id))))
          .map(
            ([key, value]) =>
              `<p class="cart-drawer__item-property"><span class="cart-drawer__item-property-name">${this.escapeHtml(key)}:</span> ${this.escapeHtml(value)}</p>`
          )
          .join('');

        const itemHasCompare = this.showCompareAt && (item.compare_at_line_price || 0) > (item.final_line_price || 0);
        const isGift = item.properties?._discelo_gift === 'true';
        const salePriceMarkup = `<p class="cart-drawer__item-price">${isGift && item.final_line_price === 0 ? 'OFFERT · ' : ''}${this.formatMoney(item.final_line_price)}</p>`;
        const comparePriceMarkup = itemHasCompare
          ? `<p class="cart-drawer__item-compare-price">${this.formatMoney(item.compare_at_line_price)}</p>`
          : '';
        const pricesMarkup =
          this.priceOrder === 'compare_first' && itemHasCompare
            ? `${comparePriceMarkup}${salePriceMarkup}`
            : `${salePriceMarkup}${comparePriceMarkup}`;
        const itemSavingsMarkup =
          this.showSavings && itemHasCompare
            ? `<p class="cart-drawer__item-savings">${this.escapeHtml(
                this.savingsItemTemplate.replace('[[amount]]', this.formatMoney(item.compare_at_line_price - item.final_line_price))
              )}</p>`
            : '';

        const isPending = this.pendingItemKeys.has(item.key);

        return `
          <li class="cart-drawer__item${isPending ? ' is-pending' : ''}" data-cart-item-key="${item.key}" aria-busy="${isPending ? 'true' : 'false'}">
            ${imageMarkup}
            <div class="cart-drawer__item-content">
              <div class="cart-drawer__item-top">
                <div>
                  <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
                  ${variantMarkup}
                  ${item.properties?._discelo_gift === 'true' ? `<p class="cart-drawer__item-property">Quantité : ${item.quantity}</p>` : ''}
                  ${propertiesMarkup}
                </div>
                <div class="cart-drawer__item-prices">
                  ${pricesMarkup}
                </div>
                ${itemSavingsMarkup}
              </div>
              <div class="cart-drawer__item-bottom" ${isGift ? 'hidden style="display:none"' : ''}>
                <div class="cart-drawer__quantity">
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>-</button>
                  <input type="number" class="cart-drawer__quantity-value" value="${item.quantity}" min="1" aria-label="${this.escapeHtml(this.quantityLabel)}" data-cart-quantity-input ${isPending ? 'disabled' : ''}>
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>+</button>
                </div>
                <button type="button" class="cart-drawer__remove" data-cart-remove ${isPending ? 'disabled' : ''} aria-label="${this.escapeHtml(this.removeLabel)}: ${this.escapeHtml(item.product_title)}"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 6h18M9 6V4h6v2M5 6l1 14h12l1-14M10 10v6M14 10v6"/></svg></button>
              </div>
            </div>
          </li>
        `;
      })
      .join('');

    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsMarkup}</ul>`;

    this.maybeClearOrphanProtection(cart);
  }

  updateShippingBar(cart) {
    if (!this.shippingEnabled || !this.shippingBar || this.shippingThreshold <= 0) return;

    const itemCount = cart.item_count || 0;
    this.shippingBar.hidden = itemCount === 0;
    if (itemCount === 0) {
      // Reset so a re-opened, freshly emptied cart doesn't briefly flash the
      // previous "unlocked" state before the first real update arrives.
      this.shippingBar.classList.remove('is-complete');
      if (this.shippingFill) this.shippingFill.style.width = '0%';
      return;
    }

    const total = cart.total_price || 0;
    const remaining = Math.max(0, this.shippingThreshold - total);
    const percent = Math.min(100, Math.round((total / this.shippingThreshold) * 100));

    if (this.shippingFill) this.shippingFill.style.width = `${percent}%`;

    if (remaining <= 0) {
      this.shippingBar.classList.add('is-complete');
      if (this.shippingMessage) this.shippingMessage.textContent = `🎉 ${this.shippingCompleteTemplate}`;
    } else {
      this.shippingBar.classList.remove('is-complete');
      if (this.shippingMessage) {
        const text = this.shippingRemainingTemplate
          .replace('[[amount]]', this.formatMoney(remaining))
          .replace('[[reward]]', this.shippingReward);
        this.shippingMessage.textContent = text;
      }
    }
  }

  updateUpsell(cart) {
    if (!this.upsellEnabled || !this.upsellSection || !this.upsellList) return;

    const itemCount = cart.item_count || 0;
    if (itemCount === 0 && this.drawer.dataset.emptyUpsells !== 'true') {
      this.upsellSection.hidden = true;
      return;
    }

    // The upsell source data is static (every variant in the store, rendered once via
    // the hidden cart-drawer-upsell section), so we only need to read it from the DOM
    // once and re-filter it locally against the cart's current variant ids - no fetch,
    // which also means this works fine on a single-product store with several variants.
    if (!this.upsellSourceNodes) {
      this.upsellSourceNodes = [...document.querySelectorAll('[data-upsell-product]')];
    }

    const cartVariantIds = new Set((cart.items || []).map((item) => Number(item.variant_id)));

    const candidates = this.upsellSourceNodes.filter((node) => {
      const variantId = Number(node.dataset.variantId);
      const isAvailable = node.dataset.available !== 'false';
      return variantId && isAvailable && !cartVariantIds.has(variantId);
    });

    if (!candidates.length) {
      this.upsellSection.hidden = true;
      return;
    }

    const products = candidates.slice(0, this.upsellLimit);

    this.upsellList.innerHTML = products
      .map((node) => {
        const productId = node.dataset.productId;
        const variantId = node.dataset.variantId;
        const title = node.dataset.title || '';
        const image = node.dataset.image || '';
        const price = node.dataset.price || '';

        // Products requiring a reciter must go through their configuration form.
        if (node.dataset.requiresReciter === 'true') {
          return `<a class="cart-drawer__upsell-card" href="${this.escapeHtml(node.dataset.url)}" aria-label="Choisir le récitateur : ${this.escapeHtml(title)}">
            <span class="cart-drawer__upsell-media">${image ? `<img src="${this.escapeHtml(image)}" alt="" loading="lazy">` : ''}</span>
            <span class="cart-drawer__upsell-title">${this.escapeHtml(title)}</span>
            <span class="cart-drawer__upsell-price">${this.escapeHtml(price)}</span>
            <span>Choisir le récitateur</span>
          </a>`;
        }

        return `
          <button
            type="button"
            class="cart-drawer__upsell-card"
            data-cart-drawer-upsell-add
            data-product-id="${this.escapeHtml(productId)}"
            data-variant-id="${this.escapeHtml(variantId)}"
            aria-label="${this.escapeHtml(this.addLabel)}: ${this.escapeHtml(title)}"
          >
            <span class="cart-drawer__upsell-media">
              ${image ? `<img src="${this.escapeHtml(image)}" alt="" loading="lazy">` : ''}
              <span class="cart-drawer__upsell-add-icon" aria-hidden="true">+</span>
            </span>
            <span class="cart-drawer__upsell-title">${this.escapeHtml(title)}</span>
            <span class="cart-drawer__upsell-price">${this.escapeHtml(price)}</span>
          </button>
        `;
      })
      .join('');

    this.upsellSection.hidden = false;
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);

    const focusTarget =
      this.panel.querySelector('#CartDrawerTitle') ||
      this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!this.showCompareAt) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!this.showCompareAt || !cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.final_price || 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Exposed on window so other cart-related scripts (e.g. the standalone
  // /cart page controller) can push cart updates through here too, keeping
  // the header cart count and the drawer itself in sync everywhere.
  window.cartDrawer = new CartDrawerController();
  window.petiteCartFeatures = new PetiteCartFeatures(window.cartDrawer);
});



class PetiteCartFeatures {
  constructor(cart) {
    this.cart = cart;
    this.drawer = cart.drawer;
    if (!this.drawer) return;
    this.root = this.drawer.querySelector('[data-cart-extras]');
    this.note = this.drawer.querySelector('[data-order-note]');
    this.status = this.drawer.querySelector('[data-extras-status]');
    this.codes = [];
    const render = cart.render.bind(cart);
    cart.render = data => { render(data); this.render(data); };
    this.root?.addEventListener('submit', event => this.submit(event));
    this.note?.addEventListener('input', () => { this.noteDirty = true; });
    this.drawer.querySelector('[data-remove-codes]')?.addEventListener('click', () => this.update({discount: ''}, 'Codes supprimés.'));
    this.drawer.querySelector('.cart-drawer__checkout-button')?.addEventListener('click', async event => {
      if (!this.noteDirty && !this.pending) return;
      event.preventDefault();
      const checkoutUrl = event.currentTarget.href;
      if (this.pending) await this.pending;
      if (this.noteDirty) {
        const result = await this.update({note: this.note.value}, 'Note enregistrée.');
        if (!result) return;
      }
      window.location.assign(checkoutUrl);
    });
    const sticky = document.querySelector('[data-sticky-cart]');
    sticky?.addEventListener('click', async () => { cart.lastActiveElement = sticky; await cart.refresh(); cart.open(); });
    this.startDeadline();
  }

  async submit(event) {
    const form = event.target;
    if (!form.matches('[data-note-form], [data-code-form]')) return;
    event.preventDefault();
    if (this.pending) return;
    if (form.matches('[data-note-form]')) {
      await this.update({note: this.note.value}, 'Note enregistrée.');
    } else {
      const input = form.querySelector('input');
      const code = input.value.trim();
      if (!code) return;
      const result = await this.update({discount: [...new Set([...this.codes, code])].join(',')});
      if (!result) return;
      const applied = (result.discount_codes || []).find(d => d.code.toLowerCase() === code.toLowerCase());
      this.status.textContent = applied?.applicable ? 'Code appliqué.' : 'Code non applicable à ce panier. Les conditions seront vérifiées au paiement.';
      if (applied?.applicable) input.value = '';
    }
  }

  async update(payload, message = '') {
    if (this.pending) return null;
    this.root?.querySelectorAll('button').forEach(b => b.disabled = true);
    if (this.status) this.status.textContent = 'Enregistrement…';
    const noteValue = payload.note;
    this.pending = (async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok || !Array.isArray(data.items)) throw new Error(data.description || 'Impossible d’enregistrer.');
        if (noteValue !== undefined && this.note.value === noteValue) this.noteDirty = false;
        this.cart.render(data);
        if (this.status) this.status.textContent = message;
        return data;
      } catch (error) {
        if (this.status) this.status.textContent = 'Enregistrement non confirmé. Vérifiez votre connexion et rouvrez le panier avant de réessayer.';
        return null;
      }
    })();
    try { return await this.pending; }
    finally { this.pending = null; this.root?.querySelectorAll('button').forEach(b => b.disabled = false); }
  }

  render(data) {
    const sticky = document.querySelector('[data-sticky-cart]');
    if (sticky) { sticky.hidden = !data.item_count; sticky.querySelector('span').textContent = data.item_count; }
    if (this.note && !this.noteDirty) this.note.value = data.note || '';
    this.codes = (data.discount_codes || []).filter(c => c.applicable).map(c => c.code);
    const remove = this.drawer.querySelector('[data-remove-codes]');
    if (remove) remove.hidden = !this.codes.length;
    const list = this.drawer.querySelector('[data-applied-discounts]');
    if (list) {
      const discounts = new Map();
      (data.cart_level_discount_applications || []).forEach(d => discounts.set(d.title, d.total_allocated_amount));
      (data.items || []).forEach(item => (item.line_level_discount_allocations || []).forEach(d => {
        const title = d.discount_application.title;
        discounts.set(title, (discounts.get(title) || 0) + d.amount);
      }));
      list.replaceChildren();
      discounts.forEach((amount, title) => {
        const row = document.createElement('p'); row.className = 'pb-discount';
        row.textContent = `${title} · −${this.cart.formatMoney(amount)}`; list.append(row);
      });
      list.hidden = !discounts.size;
    }
    this.drawer.querySelectorAll('[data-cart-item-key]').forEach(row => {
      const item = data.items.find(i => i.key === row.dataset.cartItemKey);
      if (!item || item.final_line_price !== 0 || item.original_line_price <= 0) return;
      const price = row.querySelector('.cart-drawer__item-price');
      if (price) { price.textContent = 'OFFERT'; price.classList.add('pb-free'); }
    });
  }


  startDeadline() {
    const node = this.drawer.querySelector('[data-promotion-deadline]');
    if (!node) return;
    const date = Date.parse(node.dataset.promotionDeadline);
    if (!Number.isFinite(date)) return;
    const tick = () => {
      const seconds = Math.max(0, Math.floor((date - Date.now()) / 1000));
      node.hidden = seconds === 0;
      if (!seconds) { clearInterval(this.timer); return; }
      const days = Math.floor(seconds / 86400);
      const h = String(Math.floor(seconds % 86400 / 3600)).padStart(2, '0');
      const m = String(Math.floor(seconds % 3600 / 60)).padStart(2, '0');
      const s = String(seconds % 60).padStart(2, '0');
      node.querySelector('time').textContent = `${days ? days + 'j ' : ''}${h}:${m}:${s}`;
    };
    tick(); this.timer = setInterval(tick, 1000);
  }
}
